import React, { useEffect, useRef, useState } from 'react';
import { IonRefresher, IonRefresherContent } from '@ionic/react';
import { Capacitor } from '@capacitor/core';

type PositionedRefresherProps = React.ComponentProps<typeof IonRefresher> & {
    titleRef: React.RefObject<HTMLElement | null>;
    contentRef?: React.RefObject<HTMLElement | null>;
    /** pixel offset relative to title bottom (can be negative) */
    offset?: number;
    /** anchor point on the title to measure from ('bottom' or 'top') */
    anchor?: 'bottom' | 'top';
};

/**
 * PositionedRefresher
 * - Renders an `IonRefresher` (must be a direct child of `IonContent`) and
 *   keeps its visual `top` positioned relative to a provided `titleRef`.
 * - Use when you need the refresher indicator to appear under/above a title card.
 */
const PositionedRefresher: React.FC<PositionedRefresherProps> = ({
    titleRef,
    contentRef,
    offset,
    children,
    ...rest
}) => {
    const refresherRef = useRef<HTMLElement | null>(null);
    const [isNative] = useState(() => Capacitor.isNativePlatform());
    const resolvedOffset = offset ?? 0;

    useEffect(() => {
        const update = () => {
            const titleEl = titleRef?.current;
            const containerEl = contentRef?.current;
            const refEl = refresherRef.current;
            if (!titleEl || !refEl || !containerEl) return;

            const titleRect = titleEl.getBoundingClientRect();
            const containerRect = containerEl.getBoundingClientRect();
            const containerScrollTop = containerEl.scrollTop || 0;

            // titlePosInContainer is the title bottom relative to the container's scroll origin.
            // On web, slot="fixed" positions relative to IonContent so container-relative coords
            // are correct. On native, position:fixed is truly viewport-relative, so we add
            // containerRect.top to compensate (replaces the old hardcoded +24 with the real value).
            const titleBottomInViewport = titleRect.top + titleRect.height;
            const titlePosInContainer = titleBottomInViewport - containerRect.top + containerScrollTop;
            const nativeOffset = isNative ? containerRect.top : 0;
            const topPx = Math.round(titlePosInContainer + nativeOffset + resolvedOffset);

            refEl.style.setProperty('top', `${topPx}px`, 'important');
        };

        update();

        const ResizeObs = (window as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
        const ro = ResizeObs ? new ResizeObs(update) : null;
        if (ro && titleRef.current) ro.observe(titleRef.current);
        window.addEventListener('resize', update);

        return () => {
            try { ro?.disconnect(); } catch { }
            window.removeEventListener('resize', update);
        };
    }, [titleRef, contentRef, resolvedOffset, offset, isNative]);


    useEffect(() => {
        const el = refresherRef.current;
        if (!el) return;
        el.style.setProperty('box-shadow', 'none', 'important');
        
        // Use position: fixed on web for container-relative positioning
        el.style.setProperty('position', 'fixed', 'important');
    }, []);

    return (
        <IonRefresher
            ref={(el) => { refresherRef.current = el as unknown as HTMLElement; }}
            slot="fixed"
            {...rest}
        >
            {children ?? <IonRefresherContent />}
        </IonRefresher>
    );
};

export default PositionedRefresher;
